import React, { CSSProperties, Fragment, FunctionComponent, useState } from "react";
import styles from "./Mundo.module.scss";
import MundoProps from "./MundoProps.interface";

import Action from "./components/Action/Action";
import Bottle from "./components/Bottle/Bottle";
import Section from "./components/Section/Section";
import setFooterStyles from "./hooks/setFooterStyles";

import { useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import Dictionary from "components/Dictionary/Dictionary";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import isBrowser from "helpers/isBrowser";
import useTranslation from "components/Dictionary/hooks/useTranslation";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import isBrowserContext from "helpers/isBrowserContext";

const Mundo: FunctionComponent<MundoProps> = (props): JSX.Element => {
    const wrapperStyles = useWrapperStyles(props.settings as any);

    const settings = useSelector(state => state.settings.content);

    let footer: any;
    if (settings?.theme?.footer && settings?.theme?.footer.length > 0) {
        footer = settings?.theme?.footer?.[0]?.content;
    }

    const footerStyles = setFooterStyles(footer);

    const [page, gotoPage] = useState(0);
    const [animating, toggleAnimating] = useState(false);
    const [wiggle, toggleWiggle] = useState(false);
    const [swipe, updateTouch] = useState(0);
    const [offset, updateOffset] = useState({ x: 0, y: 0 });
    const [bottleScroll, updateBottleScroll] = useState(0);
    const [totalBottleScroll, setTotalBottleScroll] = useState(0);
    const [started, updateStarted] = useState(false);

    const translations = useTranslation("MUNDO.SECTION_2.RECIPES", "MUNDO.SECTION_3.THE_PROCESS", "MUNDO.SECTION_4.MUNDO_EVENTS", "MUNDO.SECTION_5.WAYS_OF_ENJOYING", "MUNDO.SECTION_6.THE_CRAFT", "MUNDO.SECTION_7.THE_DESTILLERY", "MUNDO.SECTION_8.THE_HISTORY");

    const center = {
        x: isBrowserContext() ? window.innerWidth / 2 : 0,
        y: isBrowserContext() ? window.innerHeight / 2 : 0
    };

    const onWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
        //if deltaY is 100 or -100, is a mouse wheel. If it's below it's a touchpad.
        if (page === 0) {
            updateStarted(true);
            if (e.deltaY > 6 && !animating) {
                if (bottleScroll < 27) {
                    updateBottleScroll(bottleScroll => bottleScroll + 1);
                }
                if (bottleScroll === 27) {
                    changePage(100);
                }
            }
            else if (e.deltaY < -6) {
                if (bottleScroll > 0 && !animating) {
                    updateBottleScroll(bottleScroll => bottleScroll - 1);
                }
            }
            else { }
        }
        else {
            changePage(e.deltaY);
        }
    };

    const changePage = (dir: number) => {
        if (dir > 0) {
            if (!animating) {
                if (page > 6) {
                    return false;
                }

                gotoPage(page => page + 1);
                toggleAnimating(true);

                setTimeout(() => {
                    toggleAnimating(false);
                }, 1500);
            }
        }
        else {
            if (!animating) {
                if (page === 0) {
                    if (!wiggle) {
                        toggleWiggle(true);
                    }

                    return false;
                }

                gotoPage(page => page - 1);
                toggleAnimating(true);
                setTimeout(() => { toggleAnimating(false); }, 1500);
            }
        }
    };

    const onTouch: React.TouchEventHandler<HTMLDivElement> = (e) => {
        const y = e.changedTouches?.[0].clientY;

        updateStarted(true);

        if (e.type === "touchend") {
            if (page === 0 && bottleScroll < 27 && totalBottleScroll < 27) {
                setTotalBottleScroll(totalBottleScroll + bottleScroll);
            }
            else {
                if (y - swipe > 80) {
                    changePage(-100);
                }
                if (y - swipe < -80) {
                    changePage(1);
                }
            }
        }

        if (e.type === "touchstart") {
            updateTouch(y);
        }
    };

    const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
        const y = e.changedTouches?.[0].clientY;
        const val = (y - swipe);
        const frameCalc = Math.round(val / 10);

        updateBottleScroll(-frameCalc);
    };

    const onMove = (e: React.MouseEvent) => {
        const m = { x: Math.round((e.clientX - center.x) / 10), y: Math.round((e.clientY - center.y) / 10) };
        updateOffset({ x: m.x, y: m.y });
    };

    const parallaxMover = (param?: number, reverse?: boolean): CSSProperties => {
        if (animating) {
            return {};
        }

        if (param) {
            if (reverse) {
                return {
                    transform: `translateX(${Math.round((offset.x / param))}px) translateY(${Math.round((offset.y / param))}px)`,
                    transition: "all 1s ease-out"
                };
            }

            return {
                transform: `translateX(${-Math.round((offset.x / param))}px) translateY(${-Math.round((offset.y / param))}px)`,
                transition: "all 1s ease-out"
            };
        }

        if (reverse) {
            return {
                transform: `translateX(${Math.round(offset.x)}px) translateY(${Math.round(offset.y)}px) `,
                transition: "all 1s ease-out"
            };
        }

        return {
            transform: `translateX(${-Math.round(offset.x)}px) translateY(${-Math.round(offset.y)}px) `,
            transition: "all 1s ease-out"
        };
    };

    const getBottleState = () => {
        switch (page) {
            case 0:
                if (started) {
                    return "";
                }
                return "intro";
            case 1:
                return "blur";
            case 2:
                return "plants";
            case 3:
                return "beach";
            case 4:
                return "dining";
        }

        if (page > 4) {
            return "out";
        }

        return "";
    };

    const getSectionState = (num: number) => {
        if (page === num) {
            return "active";
        }
        if (page > num) {
            return "up";
        }
        return "";
    };

    const getElementState = (num: number) => {
        if (num === 4 && page === 5) {
            return styles.left;
        }
        if (num === 4 && page > 5) {
            return styles.left;
        }
        if (page === num) {
            return styles.active;
        }
        if (num === 2 && page === 3) {
            return styles.wayUp;
        }
        if (page > num) {
            return styles.up;
        }

        return "";
    };

    return (
        <Fragment>
            <div className={styles.mainApp} onWheel={onWheel} onTouchStart={onTouch} onTouchEnd={onTouch} onTouchMove={(e) => { if (page === 0) { onTouchMove(e); } }}
                onMouseMove={(e) => {
                    if (page === 1 || page === 2 || page === 3 || page === 7) {
                        onMove(e);
                    }
                }} style={wrapperStyles}>

                <Bottle style={page === 1 || page === 2 ? parallaxMover(page === 1 ? 8 : 4) : {}}
                    animating={animating}
                    wiggle={wiggle}
                    state={getBottleState()}
                    frame={bottleScroll}
                    active={bottleScroll > 0 && bottleScroll < 27 && page === 0}
                    done={(e) => {
                        //toggleWiggle(e);
                    }} />

                <Section className="intro" state={page === 0 ? "active" : "up"}>
                    <p><Dictionary name="MUNDO.SECTION_1.MUNDO_TEQUILA" html={true} /></p>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_1.THATS_THE_SPIRIT" html={true} />
                    </DynamicHeading>
                    <svg className={styles.introArrow} width="32" height="32" viewBox="0 0 64 64" style={{
                        fillRule: "evenodd", clipRule: "evenodd", strokeLinecap: "square", position: "absolute",
                        bottom: "80px",
                        left: "50%",
                        transform: "rotate(90deg) translateY(25%)"
                    }}>
                        <g transform="matrix(1.83018,0,0,1.83018,-746.931,-606.703)">
                            <path d="M409.32,349.337L441.886,349.337L431.368,338.819L441.886,349.337L432.076,359.148" style={{ fill: "none", stroke: "#0F1E3B", strokeWidth: "3px" }} />
                        </g>
                    </svg>
                </Section>
                <Section state={getSectionState(1)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_2.HOW_DO_YOU_LIKE_IT" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_2.FOR_AN_AUTHENTIC_EXPERIENCE" html={true} /></p>
                    <div style={page === 1 ? parallaxMover(2) : {}} className={combineClasses(styles.elementCup, getElementState(1))}>
                        <img src={require("./images/mundo-mule-hand-2.webp").default} alt="" />
                    </div>
                    <div style={page === 1 ? parallaxMover(2) : {}} className={combineClasses(styles.elementGlass, getElementState(1))}>
                        <img className={styles.elementGlassTop} src={require("./images/glass-top.webp").default} alt="" />
                        <img src={require("./images/Mundo-shot-hand.webp").default} alt="" />
                    </div>

                    {props.content.buttonsSection2 && props.content.buttonsSection2.length > 0 ? (
                        <Action title={props.content.buttonsSection2?.[0].content.label || translations["MUNDO.SECTION_2.RECIPES"]} url={props.content.buttonsSection2?.[0].content.link} />
                    ) : null}
                </Section>
                <Section state={getSectionState(2)} className="white">
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_3.WHATS_THE_FUSS" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_3.BLUE_AGAVE_IS_THE_HEART_AND_SOUL" html={true} /></p>

                    {props.content.buttonsSection3 && props.content.buttonsSection3.length > 0 ? (
                        <Action title={props.content.buttonsSection3?.[0].content.label || translations["MUNDO.SECTION_3.THE_PROCESS"]} url={props.content.buttonsSection3?.[0].content.link} />
                    ) : null}
                </Section>
                <div className={combineClasses(styles.elementDirt1, getElementState(2))}>
                    <img style={page === 2 ? parallaxMover(2) : {}} src={require("./images/Agave-mundo-composition-over.webp").default} alt="" />
                </div>
                <div className={combineClasses(styles.elementDirt2, getElementState(2))}>
                    <img style={page === 2 ? parallaxMover(6) : {}} src={require("./images/Agave-mundo-composition-under.webp").default} alt="" />
                </div>

                <Section className="beach" state={getSectionState(3)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_4.MAKING_EVERY_MOMENT_COUNT" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_4.MUNDO_IS_CURIOUS_FRIENDSHIPS" html={true} /></p>
                    <div style={page === 3 ? parallaxMover(0, true) : {}} className={combineClasses(styles.flare, styles.flare1)}></div>
                    <div style={page === 3 ? parallaxMover(4, true) : {}} className={combineClasses(styles.flare, styles.flare2)}></div>
                    <div style={page === 3 ? parallaxMover(8, true) : {}} className={combineClasses(styles.flare, styles.flare3)}></div>

                    {props.content.buttonsSection4 && props.content.buttonsSection4.length > 0 ? (
                        <Action title={props.content.buttonsSection4?.[0].content.label || translations["MUNDO.SECTION_4.MUNDO_EVENTS"]} url={props.content.buttonsSection4?.[0].content.link} />
                    ) : null}

                    <div className={combineClasses(styles.volleyBall, getElementState(3))}>
                        <img src={require("./images/beach/Volley_ball.webp").default} alt="" />
                    </div>
                    <div className={combineClasses(styles.beachPerson1, getElementState(3))} style={page === 3 ? parallaxMover(2) : {}}>
                        <img src={require("./images/beach/Guy_to_the_right.webp").default} alt="" />
                    </div>
                    <div className={styles.beachPerson2}>
                        <img src={require("./images/beach/Guy_to_the_left.webp").default} alt="" style={page === 3 ? parallaxMover(6) : {}} />
                    </div>
                    <div className={styles.beachPerson3}>
                        <img src={require("./images/beach/Girl_1.webp").default} alt="" style={page === 3 ? parallaxMover(8) : {}} />
                    </div>
                    <div className={styles.beachPerson4}>
                        <img src={require("./images/beach/Girl_2.webp").default} alt="" style={page === 3 ? parallaxMover(14) : {}} />
                    </div>
                    <div className={combineClasses(styles.volleyNet, getElementState(3))} style={page === 3 ? parallaxMover(10) : {}}>
                        <img src={require("./images/beach/Volley_net.webp").default} alt="Net" />
                    </div>
                    <div className={combineClasses(styles.birdie, getElementState(3))}> <img src={require("./images/beach/birdie.svg").default} alt="" /></div>
                    <div className={combineClasses(styles.birdie2, getElementState(3))}> <img src={require("./images/beach/birdie2.svg").default} alt="" /></div>

                    <div className={combineClasses(styles.beachBg, getElementState(3))} style={page === 3 ? parallaxMover(12) : {}}>
                        <div className={styles.waterBg} style={{ backgroundImage: `url(${require("./images/beach/shimmer.webp").default})` }}></div>
                        <img src={require("./images/beach/Beach_and_sea.webp").default} alt="" />
                    </div>
                    <div className={styles.beachSky} style={{ backgroundImage: `url(${require("./images/beach/sky.jpg").default})` }}>
                        <div className={combineClasses(styles.beachClouds, getElementState(3))} style={{ backgroundImage: `url(${require("./images/beach/sky.webp").default})` }}></div>
                    </div>
                </Section>
                <div className={combineClasses(styles.dune, getElementState(3))}><img src={require("./images/beach/dune.webp").default} alt="" /></div>

                <Section className="white" state={getSectionState(4)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_5.UNBOUND_BY_TRADITIONS" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_5.ALTHOUGH_MUNDO_IS_AN_ODE_TO_MEXICAN_CULTURE" html={true} /></p>

                    {props.content.buttonsSection5 && props.content.buttonsSection5.length > 0 ? (
                        <Action title={props.content.buttonsSection5?.[0].content.label || translations["MUNDO.SECTION_5.WAYS_OF_ENJOYING"]} url={props.content.buttonsSection5?.[0].content.link} />
                    ) : null}
                </Section>
                <img className={combineClasses(styles.diningCrowd, getElementState(4))} src={require("./images/Picnic.webp").default} alt="" />
                <Section state={getSectionState(5)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_6.JIMA_WHO" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_6.THE_JIMADOR_IS_THE_CENTURY_OLD_PROFESSION" html={true} /></p>

                    {props.content.buttonsSection6 && props.content.buttonsSection6.length > 0 ? (
                        <Action title={props.content.buttonsSection6?.[0].content.label || translations["MUNDO.SECTION_6.THE_CRAFT"]} url={props.content.buttonsSection6?.[0].content.link} />
                    ) : null}
                </Section>
                <img className={combineClasses(styles.diningJimador, getElementState(5))} src={require("./images/Jimador.webp").default} alt="" />
                <div className={combineClasses(styles.diningBg, getElementState(4))} style={{ backgroundImage: `url(${require("./images/Agave_field.webp").default})` }}></div>
                <div className={combineClasses(styles.cloudBg, getElementState(4))} style={{ backgroundImage: `url(${require("./images/clouds.jpg").default})` }}></div>
                <Section className="white" state={getSectionState(6)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_7.A_MIXTURE_OF_MUD" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_7.LONG_BEFORE_MUNDO" html={true} /></p>

                    {props.content.buttonsSection7 && props.content.buttonsSection7.length > 0 ? (
                        <Action title={props.content.buttonsSection7?.[0].content.label || translations["MUNDO.SECTION_7.THE_DESTILLERY"]} url={props.content.buttonsSection7?.[0].content.link} />
                    ) : null}
                </Section>
                <video className={combineClasses(styles.elementMud, getElementState(6))} src={require("./images/Destillering_vaeske_loop_composition_for_web_compressed.mp4").default} playsInline={true} autoPlay muted loop></video>
                <Section className="white" state={getSectionState(7)}>
                    <DynamicHeading headlineSize="H2" headlineWeight="H2">
                        <Dictionary name="MUNDO.SECTION_8.BIG_NAME_SMALL_TOWN" html={true} />
                    </DynamicHeading>
                    <p><Dictionary name="MUNDO.SECTION_8.MUNDO_TEQUILA_IS_MADE_IN_THE_SMALL_MEXICAN_CITY" html={true} /></p>

                    {props.content.buttonsSection8 && props.content.buttonsSection8.length > 0 ? (
                        <Action title={props.content.buttonsSection8?.[0].content.label || translations["MUNDO.SECTION_8.THE_HISTORY"]} url={props.content.buttonsSection8?.[0].content.link} />
                    ) : null}

                    <div style={page === 7 ? parallaxMover(2) : {}} className={combineClasses(styles.townBg, styles.townBg1, getElementState(7))}>
                        <div className={styles.flag}>
                            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="328" height="225" viewBox="0 0 328 225">
                                <defs>
                                    <filter id="filter-glitch-1">
                                        <feTurbulence id="glitch" type="fractalNoise" baseFrequency="0.000001000000000001" numOctaves="2" result="warp"></feTurbulence>
                                        <feDisplacementMap xChannelSelector="R" yChannelSelector="G" scale="40" in="SourceGraphic" in2="warpOffset"></feDisplacementMap>
                                        <animate href="#glitch" xlinkHref="#glitch" attributeName="baseFrequency" dur="60s" keyTimes="0;0.5;1" values="0.000001000000000001;0.02;0.000001000000000001" repeatCount="indefinite"></animate>
                                    </filter>
                                </defs>
                                <image id="img" x="0" y="0" width="328px" height="225px" href={require("./images/Mexican-flag.png").default} xlinkHref={require("./images/Mexican-flag.png").default} filter={!isBrowser("safari") ? "url(#filter-glitch-1)" : ""}></image>
                            </svg>
                        </div>
                        <img src={require("./images/town_1.webp").default} alt="" />
                    </div>
                    <img style={page === 7 ? parallaxMover(4) : {}} className={combineClasses(styles.townBg, styles.townBg2, getElementState(7))} src={require("./images/town_2.webp").default} alt="" />
                    <div style={page === 7 ? parallaxMover(8) : {}} className={combineClasses(styles.townBg, styles.townBg3, getElementState(7))}>
                        <div style={{ backgroundImage: `url(${require("./images/town_3.webp").default})` }}></div>
                    </div>
                </Section>
                <svg>
                    <filter id="turbulence" x="0" y="0" width="100%" height="100%">
                        <feTurbulence id="sea-filter" numOctaves="1" seed="1" baseFrequency="0.0002 0.0005"></feTurbulence>
                        <feDisplacementMap scale="10" in="SourceGraphic"></feDisplacementMap>
                        <animate href="#sea-filter" xlinkHref="#sea-filter" attributeName="baseFrequency" dur="60s" keyTimes="0;0.5;1" values="0.02 0.06;0.04 0.08;0.02 0.06" repeatCount="indefinite"></animate>
                    </filter>
                </svg>
            </div>

            {/* Footer */}
            <div className={styles.mundo_footer} style={footerStyles}>
                <div className={styles.bottom}>
                    <div className={styles.copyright}>&copy; {new Date().getFullYear()} Copyright Mundo Tequila</div>

                    {settings?.footerLinks && settings.footerLinks.length > 0 ? (
                        <nav>
                            {settings.footerLinks?.map((footerLink: any, index: number) => {
                                return (
                                    <Anchor key={index} className={styles.link} to={footerLink.content.link}>
                                        {footerLink.content.label}
                                    </Anchor>
                                );
                            })}
                        </nav>
                    ) : null}
                </div>
            </div>
        </Fragment>
    );
};

export default Mundo;